import { Badge, Box, Flex, SkeletonText, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  ReactFlowProvider,
  Handle,
  Position,
  isNode,
  isEdge,
  useReactFlow,
  useNodesInitialized,
} from 'reactflow';
import { useConfig } from '~/context';
import { useASNDetail, useColorToken, useColorValue } from '~/hooks';
import { Controls } from './controls';
import { useElements } from './useElements';

import type { NodeProps as ReactFlowNodeProps } from 'reactflow';
import type { RefObject } from 'react';

interface ChartProps {
  data: StructuredResponse;
}

interface NodeProps<D extends unknown> extends Omit<ReactFlowNodeProps, 'data'> {
  data: D;
}

export interface NodeData {
  asn: string;
  name: string;
  hasChildren: boolean;
  hasParents?: boolean;
}

export const Chart = (props: ChartProps): JSX.Element => {
  const { data } = props;
  const container = useRef<HTMLDivElement>(null);
  const { primaryAsn, orgName } = useConfig();

  const dots = useColorToken('colors', 'blackAlpha.500', 'whiteAlpha.400');

  const elements = useElements({ asn: primaryAsn, name: orgName }, data);

  const nodes = useMemo(() => elements.filter(isNode), [elements]);
  const edges = useMemo(() => elements.filter(isEdge), [elements]);

  return (
    <ReactFlowProvider>
      <Box ref={container} w="100%" h="100%" minH={0} minW={0} zIndex={1}>
        <ReactFlow
          fitView
          fitViewOptions={{ padding: 0.15, minZoom: 0.05, maxZoom: 1 }}
          minZoom={0.05}
          snapToGrid
          nodes={nodes}
          edges={edges}
          nodeTypes={{ ASNode }}
          edgesUpdatable={false}
          nodesDraggable={false}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <FitViewport container={container} />
          <Background color={dots} />
          <Controls />
        </ReactFlow>
      </Box>
    </ReactFlowProvider>
  );
};

// Refit after nodes are measured and whenever the available viewport changes.
const FitViewport = ({ container }: { container: RefObject<HTMLDivElement> }): null => {
  const { fitView } = useReactFlow();
  const ready = useNodesInitialized();
  useEffect(() => {
    if (!ready || !container.current) return;
    let frame = 0;
    const fit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        void fitView({ padding: 0.15, minZoom: 0.05, maxZoom: 1 });
      });
    };
    const observer = new ResizeObserver(fit);
    observer.observe(container.current);
    fit();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [ready, fitView, container]);
  return null;
};

const ASNode = (props: NodeProps<NodeData>): JSX.Element => {
  const { data } = props;
  const container = useRef<HTMLDivElement>(null);
  const { asn, name, hasChildren, hasParents } = data;

  const color = useColorValue('black', 'white');
  const bg = useColorValue('white', 'whiteAlpha.200');

  const { data: asnData, isError, isLoading } = useASNDetail(String(asn));

  return (
    <>
      {hasChildren && <Handle type="source" position={Position.Top} />}
      <Box py={2} px={3} bg={bg} w="240px" h="100px" color={color} boxShadow="md" borderRadius="md">
        <VStack spacing={1} h="100%" justify="center">
          <Flex fontSize="sm" w="100%" justify="center" textAlign="center" overflowWrap="anywhere" noOfLines={3}>
            {isLoading ? (
              <Box h={2} w={24}>
                <SkeletonText noOfLines={1} color={color} />
              </Box>
            ) : !isError && asnData?.data?.asn.organization?.orgName ? (
              asnData.data.asn.organization.orgName
            ) : (
              name
            )}
          </Flex>
          <Badge fontFamily="mono" fontWeight="normal" fontSize="sm" colorScheme="primary">
            {asn}
          </Badge>
        </VStack>
      </Box>
      {hasParents && <Handle type="target" position={Position.Bottom} />}
    </>
  );
};
